<template>
  <div class="dashboard-root">
    <!-- ─── HEADER HERO ─── -->
    <div class="dashboard-hero">
      <div class="hero-grid-overlay" />
      <v-container fluid class="hero-inner">
        <v-row align="center">
          <v-col cols="12" md="7">
            <div class="hero-eyebrow">
              <span class="dot pulse" />
              Live monitoring
            </div>
            <h1 class="hero-title">Outernet Dashboard</h1>
            <p class="hero-sub">
              Track submissions, reward states, and duplicate signals in real
              time.
            </p>
          </v-col>

          <v-col cols="12" md="5" class="d-flex justify-end align-center">
            <v-btn outlined class="action-btn-ghost mr-3" @click="resetFilter">
              <v-icon left small>mdi-filter-off-outline</v-icon>
              Clear
            </v-btn>
            <v-btn
              class="action-btn-primary"
              depressed
              @click="refreshDashboard"
            >
              <v-icon left small>mdi-refresh</v-icon>
              Refresh
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <!-- ─── MAIN BODY ─── -->
    <v-container fluid class="body-wrap">
      <!-- ─── FILTER BAR ─── -->
      <div class="filter-bar">
        <v-row align="center" no-gutters>
          <v-col cols="12" sm="6" md="2" class="filter-col">
            <label class="field-label">From</label>
            <v-menu v-model="menuFrom" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateFrom"
                  dense
                  outlined
                  hide-details
                  placeholder="Start date"
                  prepend-inner-icon="mdi-calendar-start"
                  readonly
                  class="styled-field"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker v-model="dateFrom" @input="menuFrom = false" />
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="2" class="filter-col">
            <label class="field-label">To</label>
            <v-menu v-model="menuTo" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateTo"
                  dense
                  outlined
                  hide-details
                  placeholder="End date"
                  prepend-inner-icon="mdi-calendar-end"
                  readonly
                  class="styled-field"
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker v-model="dateTo" @input="menuTo = false" />
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="2" class="filter-col">
            <label class="field-label">Sort</label>
            <v-select
              v-model="sortOrder"
              :items="sortOptions"
              dense
              outlined
              hide-details
              class="styled-field"
            />
          </v-col>

          <v-col cols="12" sm="6" md="2" class="filter-col">
            <label class="field-label">Status</label>
            <v-select
              v-model="selectedStatus"
              :items="statusOptions"
              dense
              outlined
              hide-details
              class="styled-field"
            />
          </v-col>

          <v-col cols="12" sm="6" md="2" class="filter-col d-flex align-end">
            <div class="view-toggle">
              <button
                class="toggle-btn"
                :class="{ active: !galleryView }"
                @click="galleryView = false"
              >
                <v-icon small>mdi-table</v-icon>
              </button>
              <button
                class="toggle-btn"
                :class="{ active: galleryView }"
                @click="galleryView = true"
              >
                <v-icon small>mdi-view-grid</v-icon>
              </button>
            </div>
          </v-col>

          <v-col
            cols="12"
            md="2"
            class="filter-col d-flex align-end justify-end"
          >
            <v-btn depressed class="apply-btn" @click="applyFilter">
              Apply filters
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- ─── COUNTS (API, lazy-loaded when expanded) ─── -->
      <v-expansion-panels
        v-model="countsExpanded"
        flat
        class="counts-expansion mb-4"
      >
        <v-expansion-panel>
          <v-expansion-panel-header class="counts-panel-header">
            <span class="counts-panel-title">Counts</span>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="counts-panel-body pa-0">
            <v-progress-linear
              v-if="countsLoading"
              indeterminate
              color="indigo"
              height="3"
              class="mb-3"
            />
            <v-row class="kpi-row totals-kpi-row ma-0" no-gutters>
              <v-col
                v-for="card in totalsByStatusCards"
                :key="card.label"
                cols="12"
                sm="6"
                lg="3"
                class="kpi-col"
              >
                <div class="kpi-card" :class="card.theme">
                  <div class="kpi-top">
                    <span class="kpi-label">{{ card.label }}</span>
                    <div class="kpi-icon-wrap">
                      <v-icon :color="card.iconColor" size="18">{{ card.icon }}</v-icon>
                    </div>
                  </div>
                  <div class="kpi-value">{{ card.display }}</div>
                  <div class="kpi-bar">
                    <div
                      class="kpi-bar-fill"
                      :style="{
                        width: totalsBarWidth(card.numeric) + '%',
                        background: card.barColor,
                      }"
                    />
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <!-- ─── KPI STRIP ─── -->
      <v-row class="kpi-row" no-gutters>
        <v-col
          v-for="kpi in kpiCards"
          :key="kpi.label"
          cols="12"
          sm="6"
          md=""
          class="kpi-col"
        >
          <div class="kpi-card" :class="kpi.theme">
            <div class="kpi-top">
              <span class="kpi-label">{{ kpi.label }}</span>
              <div class="kpi-icon-wrap">
                <v-icon :color="kpi.iconColor" size="18">{{ kpi.icon }}</v-icon>
              </div>
            </div>
            <div class="kpi-value">{{ kpi.value }}</div>
            <div class="kpi-bar">
              <div
                class="kpi-bar-fill"
                :style="{
                  width: kpiBarWidth(kpi.value) + '%',
                  background: kpi.barColor,
                }"
              />
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- ─── ALERT ─── -->
      <v-alert
        v-if="error"
        type="error"
        dense
        outlined
        class="mt-4"
        dismissible
        @input="error = ''"
      >
        {{ error }}
      </v-alert>

      <!-- ─── TABLE VIEW ─── -->
      <div v-if="!galleryView" class="table-wrap mt-6">
        <div class="section-header">
          <span class="section-title">Submissions</span>
          <span class="section-count"
            >{{ filteredSubmissions.length }} records</span
          >
        </div>

        <v-data-table
          :headers="headers"
          :items="filteredSubmissions"
          :items-per-page="10"
          :footer-props="{ 'items-per-page-options': [5, 10, 25, 50] }"
          class="outernet-table"
          must-sort
        >
          <!-- Image -->
          <template v-slot:item.imageUrl="{ item }">
            <div class="table-thumb-wrap">
              <img
                :src="item.imageUrl"
                class="table-thumb"
                @error="$event.target.src = 'https://via.placeholder.com/40'"
              />
            </div>
          </template>

          <!-- User ID -->
          <template v-slot:item.userId="{ item }">
            <span class="user-id-cell">
              <v-icon x-small class="mr-1" color="grey lighten-1"
                >mdi-account</v-icon
              >
              <span class="mono">{{ item.userId }}</span>
            </span>
          </template>

          <template v-slot:item.status="{ item }">
            <div
              class="status-pill"
              :class="{
                'pill-green': item.status === 'APPROVED',
                'pill-amber': !item.status || item.status === 'IN REVIEW',
                'pill-red': item.status === 'REJECTED',
              }"
            >
              <span class="pill-dot" />
              {{ item.status || "IN REVIEW" }}
            </div>
          </template>

          <!-- Date -->
          <template v-slot:item.createdAt="{ item }">
            <span class="date-cell">{{ formatDate(item.createdAt) }}</span>
          </template>

          <!-- No data -->
          <template v-slot:no-data>
            <div class="empty-state">
              <v-icon size="48" color="grey lighten-2"
                >mdi-inbox-outline</v-icon
              >
              <p>No submissions found for the selected range.</p>
            </div>
          </template>
        </v-data-table>
      </div>

      <!-- ─── GALLERY VIEW ─── -->
      <div v-if="galleryView" class="mt-6">
        <div class="section-header mb-4">
          <span class="section-title">Gallery</span>
          <span class="section-count"
            >{{ filteredSubmissions.length }} items</span
          >
        </div>

        <v-row v-if="filteredSubmissions.length">
          <v-col
            v-for="item in gallerySubmissions"
            :key="item._id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <div class="gallery-card">
              <div class="gallery-img-wrap">
                <v-img
                  :src="item.imageUrl"
                  aspect-ratio="1"
                  class="gallery-img"
                >
                  <template v-slot:placeholder>
                    <div class="img-placeholder">
                      <v-icon color="grey lighten-2" size="32"
                        >mdi-image-outline</v-icon
                      >
                    </div>
                  </template>
                </v-img>
                <div class="gallery-overlay">
                  <div
                    class="gallery-badge"
                    :class="{
                      'badge-green': item.status === 'APPROVED',
                      'badge-red': item.status === 'REJECTED',
                      'badge-amber':
                        item.status === 'IN REVIEW' || !item.status,
                    }"
                  >
                    <v-icon x-small class="mr-1">
                      {{
                        item.status === "APPROVED"
                          ? "mdi-check-circle"
                          : item.status === "REJECTED"
                          ? "mdi-close-circle"
                          : "mdi-clock-outline"
                      }}
                    </v-icon>
                    {{ item.status || "IN REVIEW" }}
                  </div>
                </div>
              </div>

              <div class="gallery-meta">
                <div class="gallery-userid mono">
                  <v-icon x-small color="grey">mdi-account</v-icon>
                  {{ truncate(item.userId, 16) }}
                </div>
                <div class="gallery-date">{{ formatDate(item.createdAt) }}</div>
              </div>
            </div>
          </v-col>
        </v-row>

        <div v-else class="empty-state mt-8">
          <v-icon size="48" color="grey lighten-2"
            >mdi-image-off-outline</v-icon
          >
          <p>No submissions found for the selected range.</p>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import RestResource from "../../../services/dataServiceMobileV2.js";
const service = new RestResource();

export default {
  name: "OuternetDashboard",

  data() {
    return {
      submissions: [],
      error: "",
      dateFrom: null,
      dateTo: null,
      menuFrom: false,
      menuTo: false,
      filterApplied: false,
      galleryView: false,
      sortOrder: "latest",
      selectedStatus: "APPROVED",
      sortOptions: [
        { text: "Latest first", value: "latest" },
        { text: "Oldest first", value: "oldest" },
      ],
      statusOptions: [
        { text: "Approved", value: "APPROVED" },
        { text: "In Review", value: "IN REVIEW" },
        { text: "Rejected", value: "REJECTED" },
      ],
      submissionTotals: {
        total: null,
        approved: null,
        inReview: null,
        rejected: null,
      },
      countsExpanded: null,
      countsLoading: false,

      headers: [
        { text: "Preview", value: "imageUrl", sortable: false, width: "72px" },
        { text: "User ID", value: "userId" },
        { text: "Status", value: "status", width: "130px" },
        { text: "Submitted", value: "createdAt", width: "180px" },
      ],
    };
  },

  computed: {
    filteredSubmissions() {
      let data = [...this.submissions];

      if (this.filterApplied && (this.dateFrom || this.dateTo)) {
        data = data.filter((sub) => {
          const created = new Date(sub.createdAt);
          const from = this.dateFrom ? new Date(this.dateFrom) : null;
          const to = this.dateTo ? new Date(this.dateTo + "T23:59:59") : null;
          if (from && created < from) return false;
          if (to && created > to) return false;
          return true;
        });
      }

      data.sort((a, b) =>
        this.sortOrder === "latest"
          ? new Date(b.createdAt) - new Date(a.createdAt)
          : new Date(a.createdAt) - new Date(b.createdAt),
      );

      return data;
    },

    gallerySubmissions() {
      return this.filteredSubmissions;
    },

    rewardedCount() {
      return this.filteredSubmissions.filter((s) => s.isRewarded).length;
    },
    pendingCount() {
      return this.filteredSubmissions.filter(
        (s) => s.status === "IN REVIEW", // only items under review
      ).length;
    },
    approvedCount() {
      return this.filteredSubmissions.filter((s) => s.status === "APPROVED")
        .length;
    },
    uniqueUsersCount() {
      return new Set(this.filteredSubmissions.map((s) => s.userId)).size;
    },

    totalsByStatusCards() {
      const dash = "—";
      const fmt = (n) =>
        n === null || n === undefined || Number.isNaN(Number(n))
          ? dash
          : Number(n);

      return [
        {
          label: "Total",
          display: fmt(this.submissionTotals.total),
          numeric: Number(this.submissionTotals.total) || 0,
          icon: "mdi-numeric",
          iconColor: "#6366f1",
          theme: "theme-indigo",
          barColor: "#6366f1",
        },
        {
          label: "Approved",
          display: fmt(this.submissionTotals.approved),
          numeric: Number(this.submissionTotals.approved) || 0,
          icon: "mdi-check-decagram-outline",
          iconColor: "#10b981",
          theme: "theme-green",
          barColor: "#10b981",
        },
        {
          label: "IN REVIEW",
          display: fmt(this.submissionTotals.inReview),
          numeric: Number(this.submissionTotals.inReview) || 0,
          icon: "mdi-clock-outline",
          iconColor: "#f59e0b",
          theme: "theme-amber",
          barColor: "#f59e0b",
        },
        {
          label: "Rejected",
          display: fmt(this.submissionTotals.rejected),
          numeric: Number(this.submissionTotals.rejected) || 0,
          icon: "mdi-close-circle-outline",
          iconColor: "#ef4444",
          theme: "theme-red",
          barColor: "#ef4444",
        },
      ];
    },

    kpiCards() {
      return [
        {
          label: "Total Submissions",
          value: this.filteredSubmissions.length,
          icon: "mdi-database-outline",
          iconColor: "#6366f1",
          theme: "theme-indigo",
          barColor: "#6366f1",
        },
        {
          label: "Unique Users",
          value: this.uniqueUsersCount,
          icon: "mdi-account-group-outline",
          iconColor: "#8b5cf6",
          theme: "theme-purple",
          barColor: "#8b5cf6",
        },
      ];
    },
  },

  mounted() {
    this.refreshDashboard();
  },

  watch: {
    countsExpanded() {
      if (this.isCountsPanelOpen()) {
        this.fetchSubmissionTotals();
      }
    },
  },

  methods: {
    isCountsPanelOpen() {
      const v = this.countsExpanded;
      return v === 0 || (Array.isArray(v) && v.indexOf(0) !== -1);
    },

    parseOuternetSubmissionTotals(res) {
      const empty = () => ({
        total: null,
        approved: null,
        inReview: null,
        rejected: null,
      });

      const body = res && res.data;
      if (!body || typeof body !== "object") {
        return empty();
      }

      const readNum = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };

      const pick = (obj, keys) => {
        if (!obj || typeof obj !== "object") return null;
        for (let i = 0; i < keys.length; i += 1) {
          const k = keys[i];
          if (k in obj && obj[k] != null) {
            const n = readNum(obj[k]);
            if (n !== null) return n;
          }
        }
        return null;
      };

      const accumulateFromRows = (rows) => {
        if (!Array.isArray(rows) || !rows.length) return null;
        const r0 = rows[0];
        const looksLikeStatusRow =
          r0 &&
          typeof r0 === "object" &&
          (r0.status != null ||
            r0.label != null ||
            r0.name != null ||
            r0.key != null) &&
          !(
            r0.userId != null &&
            (r0.totalSubmissions != null || r0.submissionCount != null)
          );

        if (!looksLikeStatusRow) return null;

        let approved = 0;
        let inReview = 0;
        let rejected = 0;
        rows.forEach((row) => {
          if (!row || typeof row !== "object") return;
          const raw =
            row.status ??
            row.label ??
            row.name ??
            row.key ??
            "";
          const st = String(raw)
            .trim()
            .toUpperCase()
            .replace(/_/g, " ");
          const cn = readNum(
            row.count ?? row.total ?? row.value ?? row.submissions,
          );
          const n = cn !== null ? cn : 0;
          if (st.includes("APPROVED")) approved += n;
          else if (st.includes("REJECT")) rejected += n;
          else if (st.includes("REVIEW") || st.includes("PENDING")) inReview += n;
        });
        return { total: null, approved, inReview, rejected };
      };

      const merge = (a, b) => ({
        total: a.total ?? b.total ?? null,
        approved: a.approved ?? b.approved ?? null,
        inReview: a.inReview ?? b.inReview ?? null,
        rejected: a.rejected ?? b.rejected ?? null,
      });

      const hasDataField = Object.prototype.hasOwnProperty.call(body, "data");
      const rawPayload = hasDataField && body.data != null ? body.data : body;

      if (Array.isArray(rawPayload)) {
        const fromArr = accumulateFromRows(rawPayload);
        if (fromArr) return fromArr;
      }

      const root =
        hasDataField &&
        body.data != null &&
        typeof body.data === "object" &&
        !Array.isArray(body.data)
          ? body.data
          : body;

      let total =
        pick(root, [
          "total",
          "totalCount",
          "grandTotal",
          "totalSubmissions",
          "TOTAL",
        ]);

      let approved =
        pick(root, ["approved", "approvedCount", "totalApproved"]) ??
        pick(root, ["APPROVED"]);
      let inReview =
        pick(root, [
          "inReview",
          "in_review",
          "inReviewCount",
          "totalInReview",
          "TOTAL_IN_REVIEW",
          "underReview",
          "UNDER_REVIEW",
          "pending",
          "pendingCount",
        ]) ??
        pick(root, ["IN REVIEW", "IN_REVIEW", "in review"]);
      let rejected =
        pick(root, ["rejected", "rejectedCount", "totalRejected"]) ??
        pick(root, ["REJECTED"]);

      const sc =
        root.statusCounts ||
        root.counts ||
        root.byStatus ||
        root.statuses;
      if (sc && typeof sc === "object" && !Array.isArray(sc)) {
        approved = approved ?? pick(sc, ["APPROVED", "approved"]);
        inReview =
          inReview ??
          pick(sc, [
            "IN REVIEW",
            "IN_REVIEW",
            "inReview",
            "in review",
            "PENDING",
            "pending",
          ]);
        rejected = rejected ?? pick(sc, ["REJECTED", "rejected"]);
      }

      if (Array.isArray(sc)) {
        const fromSc = accumulateFromRows(sc);
        if (fromSc) {
          return merge(
            { total, approved, inReview, rejected },
            fromSc,
          );
        }
      }

      if (Array.isArray(root.breakdown)) {
        const br = accumulateFromRows(root.breakdown);
        if (br)
          return merge({ total, approved, inReview, rejected }, br);
      }

      if (root.counts && Array.isArray(root.counts)) {
        const fc = accumulateFromRows(root.counts);
        if (fc) return merge({ total, approved, inReview, rejected }, fc);
      }

      return {
        total: total ?? null,
        approved: approved ?? null,
        inReview: inReview ?? null,
        rejected: rejected ?? null,
      };
    },

    totalsBarWidth(numeric) {
      const n = typeof numeric === "number" ? numeric : Number(numeric);
      const v = Number.isFinite(n) ? n : 0;
      const max = Math.max(
        Number(this.submissionTotals.total) || 0,
        Number(this.submissionTotals.approved) || 0,
        Number(this.submissionTotals.inReview) || 0,
        Number(this.submissionTotals.rejected) || 0,
        1,
      );
      return Math.min((v / max) * 100, 100);
    },

    async fetchSubmissionTotals() {
      if (this.countsLoading) return;
      this.countsLoading = true;
      try {
        const res = await service.getTotalOuternetSubmissions();
        this.submissionTotals = this.parseOuternetSubmissionTotals(res);
      } catch (_) {
        this.submissionTotals = {
          total: null,
          approved: null,
          inReview: null,
          rejected: null,
        };
      } finally {
        this.countsLoading = false;
      }
    },

    async refreshDashboard() {
      this.$setLoader();
      try {
        const res = await service.getAllOuternetSubmissions({
          status: this.selectedStatus,
        });
        this.submissions = res.data.data || [];
        if (this.isCountsPanelOpen()) {
          await this.fetchSubmissionTotals();
        }
        this.error = "";
      } catch (err) {
        this.error =
          err.response?.data?.error || "Failed to fetch submissions.";
      } finally {
        this.$disableLoader();
      }
    },

    async getSharedSubmissions() {
      this.$setLoader();
      try {
        const res = await service.getAllOuternetSubmissions({
          status: this.selectedStatus,
        });
        this.submissions = res.data.data || [];
      } catch (err) {
        this.error =
          err.response?.data?.error || "Failed to fetch submissions.";
      } finally {
        this.$disableLoader();
      }
    },

    formatDate(date) {
      return new Date(date).toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    truncate(str, len) {
      if (!str) return "—";
      return str.length > len ? str.slice(0, len) + "…" : str;
    },

    kpiBarWidth(value) {
      const max = Math.max(this.filteredSubmissions.length, 1);
      return Math.min((value / max) * 100, 100);
    },

    async applyFilter() {
      await this.getSharedSubmissions();
      this.filterApplied = true;
    },

    resetFilter() {
      this.dateFrom = null;
      this.dateTo = null;
      this.selectedStatus = "APPROVED";
      this.filterApplied = false;
      this.getSharedSubmissions();
    },

  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap");

/* ── ROOT ── */
.dashboard-root {
  font-family: "Sora", sans-serif;
  background: #f4f6fb;
  min-height: 100vh;
}

/* ── HERO ── */
.dashboard-hero {
  position: relative;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 60%, #1e3a5f 100%);
  padding: 48px 0 56px;
  overflow: hidden;
}

.hero-grid-overlay {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      rgba(99, 102, 241, 0.07) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(99, 102, 241, 0.07) 1px, transparent 1px);
  background-size: 40px 40px;
  mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black, transparent);
}

.hero-inner {
  position: relative;
  z-index: 1;
  padding-left: 32px !important;
  padding-right: 32px !important;
}

.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 14px;
}

.dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22d3ee;
}

.dot.pulse {
  box-shadow: 0 0 0 0 rgba(34, 211, 238, 0.6);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(34, 211, 238, 0.6);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(34, 211, 238, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(34, 211, 238, 0);
  }
}

.hero-title {
  color: #f8fafc;
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.5px;
  margin: 0 0 10px;
  line-height: 1.2;
}

.hero-sub {
  color: #64748b;
  font-size: 14px;
  margin: 0;
  font-weight: 400;
}

.action-btn-primary {
  background: linear-gradient(135deg, #6366f1, #818cf8) !important;
  color: white !important;
  font-family: "Sora", sans-serif;
  font-weight: 600;
  font-size: 13px;
  border-radius: 10px;
  padding: 0 20px;
  height: 40px;
  letter-spacing: 0;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.35) !important;
}

.action-btn-ghost {
  border-color: rgba(255, 255, 255, 0.15) !important;
  color: #94a3b8 !important;
  font-family: "Sora", sans-serif;
  font-weight: 500;
  font-size: 13px;
  border-radius: 10px;
  height: 40px;
}

/* ── BODY ── */
.body-wrap {
  padding: 0 32px 60px !important;
  margin-top: -24px;
  position: relative;
  z-index: 1;
}

/* ── FILTER BAR ── */
.filter-bar {
  background: white;
  border-radius: 16px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
}

.filter-col {
  padding: 6px 8px !important;
}

.field-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 6px;
}

.styled-field >>> .v-input__control .v-input__slot {
  border-radius: 10px !important;
  font-family: "Sora", sans-serif;
  font-size: 13px;
}

.styled-field >>> .v-input__control .v-input__slot fieldset {
  border-color: #e2e8f0 !important;
}

/* View toggle */
.view-toggle {
  display: flex;
  background: #f1f5f9;
  border-radius: 10px;
  padding: 4px;
  gap: 2px;
  height: 40px;
  align-items: center;
}

.toggle-btn {
  width: 36px;
  height: 32px;
  border-radius: 7px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s ease;
  color: #94a3b8;
}

.toggle-btn.active {
  background: white;
  color: #6366f1;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.apply-btn {
  background: #0f172a !important;
  color: white !important;
  font-family: "Sora", sans-serif;
  font-weight: 600;
  font-size: 13px;
  border-radius: 10px;
  height: 40px;
  letter-spacing: 0;
  margin-top: 17px;
}

.counts-expansion {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 4px 16px rgba(0, 0, 0, 0.04);
  background: white;
}

.counts-expansion >>> .v-expansion-panel::before {
  box-shadow: none;
}

.counts-expansion >>> .v-expansion-panel-header {
  min-height: 52px;
  padding: 12px 20px !important;
  font-family: "Sora", sans-serif;
}

.counts-expansion >>> .v-expansion-panel-content__wrap {
  padding: 0 16px 20px !important;
}

.counts-panel-title {
  font-size: 14px;
  font-weight: 700;
  color: #334155;
  letter-spacing: -0.01em;
}

.totals-kpi-row {
  margin-bottom: 0;
}

/* ── KPI STRIP ── */
.kpi-row {
  margin: 0 -8px 8px;
}

.kpi-col {
  padding: 8px !important;
  flex: 1 1 0;
  min-width: 160px;
}

.kpi-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border-left: 3px solid transparent;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.theme-indigo {
  border-left-color: #6366f1;
}
.theme-green {
  border-left-color: #10b981;
}
.theme-red {
  border-left-color: #ef4444;
}
.theme-amber {
  border-left-color: #f59e0b;
}
.theme-purple {
  border-left-color: #8b5cf6;
}

.kpi-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.kpi-label {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  letter-spacing: 0.3px;
}

.kpi-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-value {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.5px;
  margin-bottom: 12px;
  line-height: 1;
}

.kpi-bar {
  height: 3px;
  border-radius: 99px;
  background: #f1f5f9;
  overflow: hidden;
}

.kpi-bar-fill {
  height: 100%;
  border-radius: 99px;
  transition: width 0.6s ease;
  opacity: 0.7;
}

/* ── SECTION HEADER ── */
.section-header {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 16px;
}

.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.section-count {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
}

/* ── TABLE ── */
.table-wrap {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.outernet-table >>> .v-data-table__wrapper table {
  border-collapse: separate;
  border-spacing: 0;
}

.outernet-table >>> .v-data-table-header th {
  background: #f8fafc !important;
  color: #64748b !important;
  font-family: "Sora", sans-serif;
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.8px !important;
  text-transform: uppercase !important;
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 14px 16px !important;
}

.outernet-table >>> tbody tr {
  transition: background 0.15s ease;
}

.outernet-table >>> tbody tr:hover {
  background: #f8fafc !important;
}

.outernet-table >>> tbody td {
  font-family: "Sora", sans-serif;
  font-size: 13px;
  color: #334155;
  border-bottom: 1px solid #f1f5f9 !important;
  padding: 12px 16px !important;
}

.outernet-table >>> .v-data-footer {
  font-family: "Sora", sans-serif;
  font-size: 12px;
  border-top: 1px solid #f1f5f9;
}

.table-thumb-wrap {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.table-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mono {
  font-family: "JetBrains Mono", monospace;
  font-size: 12px;
  color: #475569;
}

.user-id-cell {
  display: flex;
  align-items: center;
}

.date-cell {
  font-size: 12px;
  color: #64748b;
}

/* Status pills */
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 99px;
}

.pill-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.pill-green {
  background: #d1fae5;
  color: #065f46;
}
.pill-green .pill-dot {
  background: #10b981;
}

.pill-red {
  background: #fee2e2;
  color: #991b1b;
}
.pill-red .pill-dot {
  background: #ef4444;
}

.pill-blue {
  background: #eff6ff;
  color: #1e40af;
}
.pill-blue .pill-dot {
  background: #3b82f6;
}

.pill-muted {
  background: #f1f5f9;
  color: #64748b;
}
.pill-muted .pill-dot {
  background: #94a3b8;
}

.pill-amber {
  background: #fef3c7;
  color: #92400e;
}
.pill-amber .pill-dot {
  background: #f59e0b;
}

/* ── GALLERY ── */
.gallery-card {
  border-radius: 16px;
  overflow: hidden;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: transform 0.22s ease, box-shadow 0.22s ease;
  cursor: pointer;
}

.gallery-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.1);
}

.gallery-img-wrap {
  position: relative;
  overflow: hidden;
}

.gallery-img {
  transition: transform 0.4s ease;
}

.gallery-card:hover .gallery-img {
  transform: scale(1.04);
}

.gallery-overlay {
  position: absolute;
  top: 10px;
  left: 10px;
}

.gallery-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 9px;
  border-radius: 99px;
  backdrop-filter: blur(8px);
  font-family: "Sora", sans-serif;
}

.badge-green {
  background: rgba(16, 185, 129, 0.18);
  color: #065f46;
  border: 1px solid rgba(16, 185, 129, 0.3);
}
.badge-red {
  background: rgba(239, 68, 68, 0.18);
  color: #991b1b;
  border: 1px solid rgba(239, 68, 68, 0.3);
}
.badge-amber {
  background: rgba(245, 158, 11, 0.18);
  color: #92400e;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.gallery-meta {
  padding: 12px 14px;
  border-top: 1px solid #f1f5f9;
}

.gallery-userid {
  font-size: 11.5px;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
}

.gallery-date {
  font-size: 11px;
  color: #94a3b8;
}

.img-placeholder {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
}

/* ── EMPTY STATE ── */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #94a3b8;
}

.empty-state p {
  margin-top: 12px;
  font-size: 14px;
}
</style>
